import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Header, Icon, Message, Segment } from "semantic-ui-react";
import ApplicationStayForm from "../components/form";
import { useBooking } from "../context/BookingContext";
import { useAuth } from "../context/AuthContext";

function SuccessScreen({ onReset }) {
  return (
    <Segment
      basic
      style={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem 1rem",
        background: "#f0f4f8",
      }}
    >
      <Message
        icon
        positive
        style={{
          maxWidth: 480,
          width: "100%",
          textAlign: "center",
          padding: "2.5rem 2rem",
        }}
      >
        <Icon name="check circle" size="huge" />
        <Message.Content>
          <Header as="h2" style={{ color: "#143a5a" }}>
            Application Submitted
          </Header>
          <p style={{ color: "#5a6e82", fontSize: "0.92rem", lineHeight: 1.6 }}>
            Your guest house accommodation request has been submitted
            successfully. The administration will review your application and get
            back to you shortly.
          </p>
          <Button style={{ background: "#143a5a", color: "#fff" }} onClick={onReset}>
            <Icon name="plus" />
            Submit Another Application
          </Button>
        </Message.Content>
      </Message>
    </Segment>
  );
}

function BookingFormPage() {
  const { createBooking, guestHouses } = useBooking();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [submitted,    setSubmitted]    = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState('');

  const handleSubmit = async (formData) => {
    const gh =
      guestHouses.find((g) => g.name === formData.guestHouseName) ||
      guestHouses.find((g) => g.name === formData.guestHousePreferences?.[0]) ||
      guestHouses[0];

    setSubmitting(true);
    setSubmitError('');
    try {
      await createBooking({
        userId: currentUser.id,
        userName: formData.applicantName || currentUser.fullName,
        guestHouseId: gh?.id || 1,

        /* dates */
        checkIn: formData.arrivalDate,
        checkOut: formData.departureDate,
        arrivalTime: formData.arrivalTime || "",
        departureTime: formData.departureTime || "",
        numberOfDays: formData.numberOfDays || "",

        /* counts */
        guests: parseInt(formData.roomCount) || 1,
        roomCount: formData.roomCount || "1",

        /* applicant */
        applicantName: formData.applicantName || "",
        employeeId: formData.employeeId || "",
        applicantDesignation: formData.applicantDesignation || "",
        applicantDepartment: formData.applicantDepartment || "",
        applicantMobileNumber: formData.applicantMobileNumber || "",
        emailId: formData.emailId || "",

        /* guest */
        guestName: formData.guestName || "",
        guestDesignation: formData.guestDesignation || "",
        guestMobileNumber: formData.guestMobileNumber || "",
        guestAddress: formData.guestAddress || "",
        accompanyingPersonsCount: formData.accompanyingPersonsCount || 0,

        /* preferences */
        guestHouseName: formData.guestHouseName || gh?.name || "",
        guestHousePreferences: formData.guestHousePreferences || [gh?.name || ""],
        roomRequestsByPreference: formData.roomRequestsByPreference || {},

        /* booking meta */
        bookingType: formData.bookingType || "Other",
        purpose: formData.visitPurpose || "",
        visitPurpose: formData.visitPurpose || "",
        payment: formData.paymentMode || "",
        paymentMode: formData.paymentMode || "",
        roomType: formData.roomType || "single",
        officialAttachment: formData.officialAttachment || null,
        specialRequests: "",
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <SuccessScreen onReset={() => setSubmitted(false)} />;
  }

  return (
    <Segment basic>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          borderBottom: "1px solid var(--border)",
          paddingBottom: 12,
          marginBottom: 8,
        }}
      >
        <button
          className="back-circle-btn"
          onClick={() => navigate(-1)}
          title="Back"
          style={{ flexShrink: 0, marginBottom: 0 }}
        >
          <Icon name="arrow left" />
        </button>
        <Header
          as="h1"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            margin: 0,
            fontSize: "1.25rem",
            color: "var(--navy)",
            fontFamily: '"Lora", Georgia, serif',
          }}
        >
          <Icon name="building outline" style={{ fontSize: "1.1rem", color: "var(--navy)", margin: 0 }} />
          Guest House Accommodation Request Form
        </Header>
      </div>
      {submitError && (
        <Message negative>
          <Icon name="exclamation circle" />
          {submitError}
        </Message>
      )}
      <ApplicationStayForm
        mode="edit"
        onSubmit={handleSubmit}
        disabled={submitting}
        guestHouses={guestHouses}
      />
    </Segment>
  );
}

export default BookingFormPage;
